import { Status as ClaimItemStatus } from '../claim-item/status';
import { Status as ClaimStatus } from '../status';
import { usesNewFlow } from './flow';

/** Claim-level authorization state, derived — separate from claim, job, and payment status. */
export enum State {
  /** Open and not authorized: jobs and lines can still change. */
  EDITABLE = 'EDITABLE',

  /** Closed, cancelled, or expired without authorization: read-only. */
  INACTIVE = 'INACTIVE',

  /** Authorized (frozen): no further job or line edits. */
  AUTHORIZED = 'AUTHORIZED',

  /** Authorized with at least one approved job and something to pay: payment can be prepared. */
  PAYMENT_READY = 'PAYMENT_READY',

  /** Opened in the previous claims flow: paid without an authorization. */
  NOT_REQUIRED = 'NOT_REQUIRED',
}

/** Undefined for a frozen claim whose jobs weren't loaded: the two frozen states need them. */
export function getState(claim: {
  status: ClaimStatus;
  frozen: boolean;
  isAuthorizationRequired?: boolean | null;
  items?: { status: ClaimItemStatus; deleted?: Date | null }[];
  totals?: { netPayable: number } | null;
}): State | undefined {
  if (!usesNewFlow(claim)) return State.NOT_REQUIRED;

  if (claim.frozen) {
    if (!claim.items) return undefined;

    const hasApprovedJob = claim.items.some(item => !item.deleted && item.status === ClaimItemStatus.APPROVED);
    // Authorized for $0: nothing to pay.
    const isZeroPay = claim.totals?.netPayable === 0;
    return hasApprovedJob && !isZeroPay ? State.PAYMENT_READY : State.AUTHORIZED;
  }

  return claim.status === ClaimStatus.OPEN ? State.EDITABLE : State.INACTIVE;
}
